/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { ThemeProvider } from "@cd2/theme"
import React from "react"
import { nd } from "./tokens"

/**
 * The @cd2 theme, as seen by classic form fields (Fields.*, Form2.*, selects, dates) and classic
 * buttons inside a new design page, so they pick up the new look without being rewritten.
 */
const fieldTheme = {
  fontFamily: nd.font,
  borderRadius: nd.radius.medium,
  forms: {
    border: {
      radius: nd.radius.medium,
      color: nd.color.inputBorder,
      hoverColor: nd.color.inputBorderHover,
      focusColor: nd.color.text,
    },
    label: {
      font: { size: 13, weight: nd.fontWeight.medium, color: nd.color.text },
      spacing: 4,
    },
    placeholderColor: nd.color.textDisabled,
    checkboxColor: nd.color.accent,
    fix: {
      background: nd.color.surfaceSubdued,
      color: nd.color.textSubdued,
      border: { color: nd.color.inputBorder },
    },
  },
  button: { borderRadius: nd.radius.medium, defaultButtonColor: nd.color.primary },
}

/**
 * The global classic overrides in EOSThemeProvider style inputs by class name, so they're
 * matched here by class name too, scoped to the new design container.
 */
const fieldCss = css`
  .cd2-form-input,
  .DateInput_input,
  .cd2-field-select__control {
    min-height: 36px;
    border: 1px solid ${nd.color.inputBorder};
    border-radius: ${nd.radius.medium}px;
    background: ${nd.color.surface};
    box-shadow: none;
    font-family: ${nd.font};
    font-size: ${nd.fontSize.body}px;
    color: ${nd.color.text};

    &:hover {
      border-color: ${nd.color.inputBorderHover};
    }
    &:focus,
    &:focus-within {
      border-color: ${nd.color.text};
      box-shadow: 0 0 0 1px ${nd.color.text}, 0 0 0 3px ${nd.color.focus};
      outline: none;
    }
    &.cd2-form-input--errored {
      border-color: ${nd.color.critical};
      background: ${nd.tone.critical.background}33;
    }
  }
  .cd2-field-select__control--is-focused {
    border-color: ${nd.color.text};
    box-shadow: 0 0 0 1px ${nd.color.text}, 0 0 0 3px ${nd.color.focus};
  }
  .cd2-form-input,
  .DateInput_input,
  .cd2-field-select__value-container {
    padding-top: 7px !important;
    padding-bottom: 7px !important;
    font-size: ${nd.fontSize.body}px;
  }
  .cd2-field-select__menu {
    border-radius: ${nd.radius.large}px;
    box-shadow: ${nd.shadow.popover};
    overflow: hidden;
  }
  .cd2-form2-error-message {
    color: ${nd.color.critical};
    font-size: ${nd.fontSize.small}px;
  }
  .cd2-form-prefix,
  .cd2-form-suffix {
    color: ${nd.color.textSubdued};
  }
  input[type="checkbox"],
  input[type="radio"] {
    accent-color: ${nd.color.accent};
  }
`

/**
 * Gives classic @cd2 form fields inside it the new design look: rounded inputs, grey borders, a
 * dark border and mint ring on focus, the app font. Page and Modal already wrap their content in
 * it; use it yourself only for something rendered outside both.
 */
export function FieldTheme({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider.Extend theme={fieldTheme as any}>
      <div css={fieldCss}>{children}</div>
    </ThemeProvider.Extend>
  )
}

/**
 * Stacks form fields with the standard gap. Put fields that belong together on one row (e.g.
 * quantity and price) in a `FormLayout.Group`, which wraps onto separate rows when narrow.
 */
export function FormLayout({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        display: grid;
        gap: ${nd.space.md}px;
        min-width: 0;
      `}
    >
      {children}
    </div>
  )
}

function FormLayoutGroup({
  children,
  minColumnWidth = 160,
}: {
  children: React.ReactNode
  /** Fields drop onto their own rows when they'd be narrower than this */
  minColumnWidth?: number
}) {
  return (
    <div
      css={css`
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(${minColumnWidth}px, 100%), 1fr));
        gap: ${nd.space.md}px;
      `}
    >
      {children}
    </div>
  )
}

FormLayout.Group = FormLayoutGroup

/** A native checkbox with a label and optional grey help text underneath. */
export function Checkbox({
  label,
  helpText,
  checked,
  onChange,
  disabled,
  ariaLabel,
}: {
  label: React.ReactNode
  helpText?: React.ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /** Names the checkbox for screen readers when there's no visible label, e.g. in a table cell */
  ariaLabel?: string
}) {
  return (
    <label
      css={css`
        display: flex;
        gap: ${nd.space.sm}px;
        align-items: flex-start;
        font-size: ${nd.fontSize.body}px;
        color: ${disabled ? nd.color.textDisabled : nd.color.text};
        cursor: ${disabled ? "default" : "pointer"};
      `}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={e => onChange(e.target.checked)}
        css={css`
          flex: none;
          width: 16px;
          height: 16px;
          margin: 2px 0 0;
          accent-color: ${nd.color.accent};
        `}
      />
      <span>
        {label}
        {helpText && (
          <span
            css={css`
              display: block;
              color: ${nd.color.textSubdued};
            `}
          >
            {helpText}
          </span>
        )}
      </span>
    </label>
  )
}

/** Grey help text under a field or at the bottom of a card. */
export function HelpText({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        margin-top: ${nd.space.xs}px;
        font-size: 13px;
        line-height: 18px;
        color: ${nd.color.textSubdued};
      `}
    >
      {children}
    </div>
  )
}

/**
 * An on / off switch with a label, for a filter that flips a list between views (e.g. Show
 * archived). Use Checkbox in forms; use this for instant changes.
 */
export function Toggle({
  label,
  checked,
  onChange,
}: {
  label: React.ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label
      css={css`
        display: inline-flex;
        align-items: center;
        gap: ${nd.space.sm}px;
        cursor: pointer;
        user-select: none;
      `}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        css={css`
          position: relative;
          flex: none;
          width: 32px;
          height: 20px;
          padding: 0;
          border: none;
          border-radius: ${nd.radius.full}px;
          background: ${checked ? nd.color.accent : nd.color.borderStrong};
          cursor: pointer;
          transition: background 0.15s ease;
          &::after {
            content: "";
            position: absolute;
            top: 2px;
            left: ${checked ? 14 : 2}px;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: white;
            transition: left 0.15s ease;
          }
          &:focus-visible {
            outline: 2px solid ${nd.color.focus};
            outline-offset: 2px;
          }
        `}
      />
      {label}
    </label>
  )
}
